/**
 * WCAG contrast helpers for picking an accent's button-text colour (EPD-10132).
 *
 * This module is intentionally dependency-free so it can be shared by BOTH the
 * runtime (theme.ts re-exports `accentTextFor`, used by the customize panel) and
 * the standalone preset generator (`theme-presets.generate.ts`, a plain bun
 * script). Sharing one implementation is what keeps the generated preset
 * `accentText` values and the runtime Accent control in lock-step — otherwise a
 * creator touching the Accent control could flip the button text away from the
 * shipped preset value.
 */

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const c = hex.replace("#", "");
  // Expand 3-digit shorthand (#abc → #aabbcc) before parsing. A creator can type
  // shorthand into the Accent field (HEX_INPUT_RE), and `parseInt("abc", 16)`
  // would otherwise read it as 0x000abc (near-black) and pick white button text.
  const full =
    c.length === 3
      ? c
          .split("")
          .map((x) => x + x)
          .join("")
      : c.slice(0, 6);
  const num = parseInt(full, 16);
  return { r: (num >> 16) & 0xff, g: (num >> 8) & 0xff, b: num & 0xff };
}

/**
 * WCAG relative luminance of an sRGB hex colour (0–1): linearize each channel,
 * then weight. Measures real perceived contrast rather than a luma threshold.
 */
export function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const linearize = (channel: number) => {
    const s = channel / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

/** WCAG contrast ratio between two hex colours (1–21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

// Button-text foreground candidates. We intentionally do NOT use tweakcn's
// primary-foreground, which is often low-contrast on our accent; instead we pick
// whichever yields the higher WCAG contrast against the accent. Pure black is a
// fallback only reached when neither brand-aligned candidate clears AA.
const ACCENT_TEXT_DARK = "#0F1311";
const ACCENT_TEXT_LIGHT = "#FFFFFF";
const ACCENT_TEXT_BLACK = "#000000";

/** WCAG AA contrast ratio for normal-size text. */
const AA_CONTRAST = 4.5;

/**
 * The accent's button-text colour: the higher-WCAG-contrast foreground.
 *
 * Prefer the two brand-aligned candidates (`#FFFFFF` / `#0F1311`); the better of
 * them wins as long as it already clears AA (4.5:1). When NEITHER reaches AA
 * (e.g. the Slate accent `#6366F1`, where white ≈4.47:1 and `#0F1311` ≈4.19:1),
 * also consider pure `#000000` and return whichever of the three has the highest
 * contrast ratio (for Slate `#000000` ≈4.7:1 wins and meets AA). The choice is
 * deterministic: dark wins a tie with white, and black only overrides when it is
 * strictly higher.
 */
export function accentTextFor(accent: string): string {
  const light = contrastRatio(accent, ACCENT_TEXT_LIGHT);
  const dark = contrastRatio(accent, ACCENT_TEXT_DARK);
  const better = dark >= light ? ACCENT_TEXT_DARK : ACCENT_TEXT_LIGHT;
  if (Math.max(light, dark) >= AA_CONTRAST) return better;
  const black = contrastRatio(accent, ACCENT_TEXT_BLACK);
  return black > Math.max(light, dark) ? ACCENT_TEXT_BLACK : better;
}

// ============================================================================
// Readable text over a theme background (EPD-12471)
// ============================================================================

/** Near-black / white endpoints a failing text colour is pulled toward. */
export const READABLE_TEXT_DARK = ACCENT_TEXT_DARK;
export const READABLE_TEXT_LIGHT = ACCENT_TEXT_LIGHT;

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** Steps from the creator's colour to the endpoint; 5% each keeps the hue. */
const MIX_STEPS = 20;

function toHex({ r, g, b }: { r: number; g: number; b: number }): string {
  return (
    "#" +
    [r, g, b]
      .map((v) =>
        Math.max(0, Math.min(255, Math.round(v)))
          .toString(16)
          .padStart(2, "0")
          .toUpperCase(),
      )
      .join("")
  );
}

/** Alpha (0–1) of a hex colour; only the 8-digit form carries one. */
function alphaOf(hex: string): number {
  const c = hex.replace("#", "");
  return c.length === 8 ? parseInt(c.slice(6), 16) / 255 : 1;
}

/** Mix `a` toward `b` by `t` (0 = a, 1 = b), as an opaque 6-digit hex. */
function mix(a: string, b: string, t: number): string {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  return toHex({
    r: ca.r + (cb.r - ca.r) * t,
    g: ca.g + (cb.g - ca.g) * t,
    b: ca.b + (cb.b - ca.b) * t,
  });
}

/**
 * The opaque colour a (possibly translucent) foreground shows as over `bg`.
 * Derived muted text is stored with an alpha pair (`#RRGGBB99`), and
 * `hexToRgb` ignores alpha, so contrast must be measured on the blend.
 */
export function compositeOver(fg: string, bg: string): string {
  return mix(bg, fg, alphaOf(fg));
}

/**
 * A text colour that reads on every one of `backgrounds` (WCAG AA, 4.5:1 by
 * default). The creator's colour is returned untouched when it already passes;
 * otherwise it is pulled, in small steps, toward near-black or white (whichever
 * contrasts more with the first background) until it passes, so a too-light
 * muted grey becomes a slightly darker grey rather than flipping to black.
 * When even the endpoint fails (backgrounds of opposite lightness), the
 * endpoint is returned as the best available. Invalid hex input is returned
 * as-is (the theme schema already rejects it).
 */
export function readableTextColor(
  fg: string,
  backgrounds: readonly string[],
  minRatio: number = AA_CONTRAST,
): string {
  const [first] = backgrounds;
  if (!first || !HEX_RE.test(fg) || !backgrounds.every((b) => HEX_RE.test(b)))
    return fg;

  const passes = (color: string) =>
    backgrounds.every(
      (bg) => contrastRatio(compositeOver(color, bg), bg) >= minRatio,
    );
  if (passes(fg)) return fg;

  const base = compositeOver(fg, first);
  const target =
    contrastRatio(first, READABLE_TEXT_DARK) >=
    contrastRatio(first, READABLE_TEXT_LIGHT)
      ? READABLE_TEXT_DARK
      : READABLE_TEXT_LIGHT;
  for (let step = 1; step < MIX_STEPS; step++) {
    const candidate = mix(base, target, step / MIX_STEPS);
    if (passes(candidate)) return candidate;
  }
  return target;
}
